import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  Image,
  Modal,
  Nav,
  Navbar,
} from "react-bootstrap";
import { Formik } from "formik";
import * as yup from "yup";

import { useAuth } from "@src/context";

import { ErrorHandling, filterRecord } from "@helpers/GlobalFunctions";
import MobileHeader from "./MobileHeader";
import NotificationCenter from "../NotificationCenter";
import ProfileSettingsPanel from "./ProfileSettingsPanel";

import Logo from "@assets/logo/logo.png";
import "./Header.scss";
import {
  SearchUsers,
  AddConnection,
  DeleteConnection,
  getConnectionPairStatus,
  FollowUser,
  UnfollowUser,
  IsFollowing,
} from "@src/api/users";
import { Avatar } from "../Avatar";
import { toast } from "../ToastExtended";

const Header = () => {
  const { routes, isLogin, userData } = useAuth();
  const [show, setShow] = useState(false);
  const [showMobileProfileDropdown, setShowMobileProfileDropdown] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const mobileProfileRef = useRef(null);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const handleMobileSearchToggle = () => {
    setShowMobileSearch(!showMobileSearch);
    if (showMobileProfileDropdown) {
      setShowMobileProfileDropdown(false);
    }
  };
  const handleMobileSearchClose = () => {
    setShowMobileSearch(false);
  };

  // Handle outside click for mobile profile dropdown
  React.useEffect(() => {
    const handler = (event) => {
      if (
        showMobileProfileDropdown &&
        mobileProfileRef.current &&
        !mobileProfileRef.current.contains(event.target)
      ) {
        setShowMobileProfileDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showMobileProfileDropdown]);

  // Get avatar URL for profile menu
  const getAvatarUrl = () => {
    if (userData?.get_avatar?.public_url) {
      return userData.get_avatar.public_url;
    }
    if (userData?.role === 'employer' && userData?.employer_profile?.com_logo_file?.public_url) {
      return userData.employer_profile.com_logo_file.public_url;
    }
    return null;
  };

  // Get display name for profile menu
  const getDisplayName = () => {
    if (userData?.role === 'candidate') {
      const firstName = userData?.user_profile?.first_name || '';
      const lastName = userData?.user_profile?.last_name || '';
      return `${firstName} ${lastName}`.trim() || userData?.email || 'User';
    } else if (userData?.role === 'employer') {
      return userData?.employer_profile?.com_name || userData?.email || 'Company';
    }
    return userData?.email || 'User';
  };

  const profilePath = userData?.role === 'candidate' ? '/candidate-profile' : '/employer-profile';

  return (
    <React.Fragment>
      <Navbar
        bg="white"
        expand="lg"
        sticky="top"
        className="m-lg-0 m-3 nav-shadow"
      >
        <div className="container-fluid px-lg-5 px-sm-3">
          <Button
            onClick={handleShow}
            variant={"light"}
            className="toggle-menu d-lg-none d-block"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </Button>
          <Navbar.Brand as={Link} to="/" className="d-lg-block d-none">
            <Image src={Logo} fluid width={100} />
          </Navbar.Brand>
          <div className="d-flex align-items-center justify-content-center gap-2">
            {/* Mobile Search - Icon or Full Bar */}
            {isLogin && (
              <>
                {!showMobileSearch ? (
                  <Button
                    variant="light"
                    className="d-lg-none d-flex align-items-center justify-content-center border-0 p-2"
                    onClick={handleMobileSearchToggle}
                    style={{ minWidth: '40px', height: '40px' }}
                  >
                    <svg
                      width="20"
                      height="20"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                    </svg>
                  </Button>
                ) : (
                  <div className="d-lg-none d-flex align-items-center flex-grow-1 me-2">
                    <SearchInput
                      id="formBasicMobileSearch"
                      className="flex-grow-1"
                      onSearchClose={handleMobileSearchClose}
                    />
                  </div>
                )}
              </>
            )}
            {/* Notification center - visible on mobile/tablet (when nav is collapsed) */}
            {isLogin && !showMobileSearch && (
              <div className="d-lg-none">
                <NotificationCenter />
              </div>
            )}
            {/* Mobile Profile Header */}
            {isLogin && !showMobileSearch && (
              <div 
                className="d-lg-none position-relative ms-2" 
                ref={mobileProfileRef}
                style={{ minWidth: 'fit-content' }}
              >
                <button
                  type="button"
                  onClick={() => setShowMobileProfileDropdown(!showMobileProfileDropdown)}
                  className="d-flex align-items-center border-0 bg-transparent p-0"
                  style={{ cursor: 'pointer' }}
                >
                  <Avatar 
                    src={getAvatarUrl()} 
                    username={getDisplayName()} 
                    size="sm" 
                    center={true} 
                  />
                  <div className="ms-2 d-none d-sm-block">
                    <p className="mb-0 fw-bold text-dark" style={{ fontSize: '12px', lineHeight: '1.2' }}>{getDisplayName()}</p>
                    <p className="mb-0 text-muted" style={{ fontSize: '10px', lineHeight: '1.2' }}>{userData?.email}</p>
                  </div>
                </button>
                {/* Mobile Profile Dropdown */}
                {showMobileProfileDropdown && (
                  <MobileProfileDropdown
                    userData={userData}
                    profilePath={profilePath}
                    getAvatarUrl={getAvatarUrl}
                    getDisplayName={getDisplayName}
                    routes={routes}
                    onClose={() => setShowMobileProfileDropdown(false)}
                  />
                )}
              </div>
            )}
          </div>
          <Navbar.Collapse
            id="navbar--nav"
            data-animations="fadeInUp fadeInLeft fadeInUp fadeInRight"
          >
            <Nav className="ms-auto flex-wrap justify-content-end align-items-center">
              {isLogin && (
                <Nav.Item className="d-lg-block d-none mt-xl-0 mt-3 me-2">
                  <SearchInput id="formBasicDesktopSearch" />
                </Nav.Item>
              )}
              {routes.map((menu, index) => {
                const depthLevel = 0;
                if (menu && !menu.hidden) {
                  return (
                    <MenuItems
                      items={menu}
                      key={index}
                      depthLevel={depthLevel}
                    />
                  );
                } else {
                  return null;
                }
              })}
              {isLogin && (
                <Nav.Item className="ms-2 d-none d-lg-flex align-items-center">
                  <NotificationCenter />
                </Nav.Item>
              )}
            </Nav>
          </Navbar.Collapse>
        </div>
        <Modal
          show={show}
          onHide={handleClose}
          className="nav-left d-lg-none d-block"
          dialogClassName="position-fixed m-auto"
          contentClassName="h-100 border-0 rounded-0"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Image src={Logo} fluid width={100} />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <MobileHeader routes={routes} handleClose={handleClose} />
          </Modal.Body>
        </Modal>
      </Navbar>
    </React.Fragment>
  );
};
const MenuItems = ({ items, depthLevel, isInProfileDropdown = false }) => {
  const [dropdown, setDropdown] = useState(false);
  const [toggled, setToggle] = useState(false);
  const { pathname } = useLocation();
  const { userData } = useAuth();

  const navItemRef = useRef();
  const isChildToggled = useRef(false);

  React.useEffect(() => {
    const handler = (event) => {
      if (
        dropdown &&
        navItemRef.current &&
        !navItemRef.current.contains(event.target)
      ) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [dropdown]);

  React.useEffect(() => {
    if (!isChildToggled.current) {
      isChildToggled.current = true;
      if (items.children && !toggled) {
        if (filterRecord(items.children, "link", pathname)) {
          setToggle(true);
        }
      }
    }
  }, [items.children, toggled, pathname]);

  const onMouseEnter = () => {
    if (window.innerWidth > 960) {
      if (items.isProfileMenu) {
        // For profile menu: open on hover, but don't close on mouse leave
        setDropdown(true);
      } else if (!isInProfileDropdown) {
        // For other menus outside profile dropdown: normal hover behavior
        // Inside profile dropdown: no hover expansion
        setDropdown(true);
      }
    }
  };

  const onMouseLeave = () => {
    if (window.innerWidth > 960) {
      if (!items.isProfileMenu && !isInProfileDropdown) {
        // Only close on mouse leave for non-profile menus outside profile dropdown
        // Profile menu stays open until outside click
        setDropdown(false);
      }
    }
  };

  // Get avatar URL for profile menu
  const getAvatarUrl = () => {
    if (userData?.get_avatar?.public_url) {
      return userData.get_avatar.public_url;
    }
    if (userData?.role === 'employer' && userData?.employer_profile?.com_logo_file?.public_url) {
      return userData.employer_profile.com_logo_file.public_url;
    }
    return null;
  };

  // Get display name for profile menu
  const getDisplayName = () => {
    if (userData?.role === 'candidate') {
      const firstName = userData?.user_profile?.first_name || '';
      const lastName = userData?.user_profile?.last_name || '';
      return `${firstName} ${lastName}`.trim() || userData?.email || 'User';
    } else if (userData?.role === 'employer') {
      return userData?.employer_profile?.com_name || userData?.email || 'Company';
    }
    return userData?.email || 'User';
  };

  const handleToggleClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDropdown((prev) => !prev);
  };

  const isOpen = dropdown || toggled;

  return (
    <Nav.Item
      className="menu-items my-xl-0 my-lg-2"
      ref={navItemRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {items.children ? (
        <React.Fragment>
          <div className="menu-item-row w-100">
            {items.isProfileMenu ? (
              <Nav.Link
                type="button"
                aria-haspopup="menu"
                className={`menu-item-link profile-menu-trigger ${isOpen ? "active" : ""}`}
                aria-expanded={dropdown ? "true" : "false"}
                onClick={handleToggleClick}
              >
                <div className="d-flex align-items-center">
                  <Avatar 
                    src={getAvatarUrl()} 
                    username={getDisplayName()} 
                    size="sm" 
                    center={true} 
                  />
                </div>
              </Nav.Link>
            ) : (
              <Nav.Link
                type="button"
                aria-haspopup="menu"
                className={`menu-item-link menu-item-expandable ${isOpen ? "active" : ""}`}
                aria-expanded={dropdown ? "true" : "false"}
                onClick={handleToggleClick}
              >
                <span className="menu-item-expandable-label">{items.text}</span>
                <span
                  className={`menu-item-expandable-chevron arrow ${depthLevel > 0 ? "arrow-right" : ""} ${isOpen ? "arrow-open" : ""}`}
                  aria-hidden
                />
              </Nav.Link>
            )}
          </div>
          {items.isProfileMenu ? (
            <ProfileDropdown
              submenus={items.children}
              dropdown={dropdown}
              userData={userData}
              getAvatarUrl={getAvatarUrl}
              getDisplayName={getDisplayName}
              onKeepOpen={() => setDropdown(true)}
            />
          ) : (
            <Dropdown
              depthLevel={depthLevel}
              submenus={items.children}
              dropdown={dropdown}
              isInProfileDropdown={isInProfileDropdown}
            />
          )}
        </React.Fragment>
      ) : !items.button ? (
        <Nav.Link 
          as={NavLink} 
          to={items.link} 
          className={`menu-item-link ${items.isLogout ? 'logout-link' : ''} ${depthLevel > 0 ? '' : 'mx-3'}`}
        >
          {items.text}
        </Nav.Link>
      ) : (
        <Button as={Link} to={items.link} className="btn btn-primary mx-3">
          {items.text}
        </Button>
      )}
    </Nav.Item>
  );
};

const ProfileDropdown = ({ submenus, dropdown, userData, getAvatarUrl, getDisplayName, onKeepOpen }) => {
  const profilePath = userData?.role === 'candidate' ? '/candidate-profile' : '/employer-profile';

  const handleDropdownClick = (e) => {
    e.stopPropagation();
  };

  const handleMouseEnter = () => {
    if (onKeepOpen && window.innerWidth > 960) {
      onKeepOpen();
    }
  };

  return (
    <Nav
      className={`navbar-dropdown p-0 flex-column profile-dropdown ${
        dropdown ? "show" : ""
      }`}
      style={{ minWidth: "304px" }}
      onClick={handleDropdownClick}
      onMouseEnter={handleMouseEnter}
    >
      <ProfileSettingsPanel
        submenus={submenus}
        userData={userData}
        getAvatarUrl={getAvatarUrl}
        getDisplayName={getDisplayName}
        profilePath={profilePath}
      />
    </Nav>
  );
};

const Dropdown = ({ submenus, dropdown, depthLevel, isInProfileDropdown = false }) => {
  depthLevel = depthLevel + 1;
  const dropdownClass = depthLevel > 1 ? "dropdown-submenu" : "";
  return (
    <Nav
      className={`navbar-dropdown flex-column ${
        dropdown ? "show" : ""
      } ${dropdownClass} ${isInProfileDropdown ? "navbar-dropdown--profile-nested" : "pe-3"} `}
    >
      {submenus.map((submenu, index) => (
        <MenuItems items={submenu} key={index} depthLevel={depthLevel} isInProfileDropdown={isInProfileDropdown} />
      ))}
    </Nav>
  );
};

const MobileProfileDropdown = ({ userData, profilePath, getAvatarUrl, getDisplayName, routes, onClose }) => {
  const profileMenu = routes.find((menu) => menu.isProfileMenu);

  return (
    <div
      className="mobile-profile-dropdown position-absolute"
      style={{
        top: "100%",
        right: 0,
        zIndex: 1050,
        marginTop: "8px",
        maxHeight: "80vh",
        overflowY: "auto",
      }}
    >
      <ProfileSettingsPanel
        submenus={profileMenu?.children || []}
        userData={userData}
        getAvatarUrl={getAvatarUrl}
        getDisplayName={getDisplayName}
        profilePath={profilePath}
        onClose={onClose}
      />
    </div>
  );
};

const SearchInput = ({ className = "", id = "formBasicSearch", onSearchClose = null }) => {
  const { userData: user } = useAuth();
  const [resultItems, setResultItems] = useState([]);
  const [followingStatus, setFollowingStatus] = useState({});
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef();
  const connections = useMemo(() => {
    if (user && user.my_connection_ids && user.my_connection_ids.length > 0) {
      return user.my_connection_ids;
    }

    return [];
  }, [user]);
  useEffect(() => {
    const handleClick = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, []);

  const schema = yup.object().shape({
    query: yup.string().trim().required().min(3),
  });

  const navigate = useNavigate();
  const handleInputChange = (e, handleChange) => {
    handleChange(e);
    if (!e.target.value.trim()) {
      return;
    }
    if (searchTimeout) clearTimeout(searchTimeout);

    setSearchTimeout(
      setTimeout(async () => {
        try {
          const response = await SearchUsers(e.target.value);
          let search_result = response.data.slice(0, 3).map((v) => ({
            ...v,
            is_connected: v.is_connected || connections.some(
              (connection) => connection === v.id
            ),
            connection_status: v.connection_status || null,
            pending_connection_id: v.pending_connection_id ?? v.connection_id ?? null,
          }));
          const currentIsCandidate =
            user?.user_profile != null && user?.employer_profile == null;
          if (currentIsCandidate && search_result.length > 0) {
            await Promise.all(
              search_result.map(async (item) => {
                if (item.role !== "candidate" || item.id === user?.id) return;
                if (item.connection_status !== "request_sent" || item.pending_connection_id)
                  return;
                try {
                  const pr = await getConnectionPairStatus(item.id);
                  const d = pr.data;
                  if (d?.status === "pending" && d.is_outgoing && d.connection_id) {
                    item.pending_connection_id = d.connection_id;
                  }
                } catch (_) {}
              }),
            );
          }
          setResultItems(search_result);
          
          // Check following status for each result
          const followingChecks = search_result.map(async (item) => {
            if (item.id !== user?.id) {
              try {
                const followResponse = await IsFollowing(item.id);
                return { userId: item.id, isFollowing: followResponse.data.is_following };
              } catch (error) {
                return { userId: item.id, isFollowing: false };
              }
            }
            return null;
          });
          
          const followResults = await Promise.all(followingChecks);
          const followMap = {};
          followResults.forEach(result => {
            if (result) {
              followMap[result.userId] = result.isFollowing;
            }
          });
          setFollowingStatus(followMap);
        } catch (error) {
          console.log(error);
        }
      }, 200)
    );
  };

  return (
    <div className="position-relative" ref={searchContainerRef}>
      <Formik
        initialValues={{ query: "" }}
        validationSchema={schema}
        onSubmit={(values, { setSubmitting }) => {
          navigate(`/search-users?query=${values.query}`);
          setSubmitting(false);
          if (onSearchClose) {
            onSearchClose();
          }
        }}
      >
        {({ handleChange, handleSubmit, isSubmitting, values }) => (
          <Form
            autoComplete="off"
            onSubmit={handleSubmit}
            className={className}
          >
            <Form.Group className={`form-group has-search ${onSearchClose ? 'has-close-btn' : ''}`} controlId={id}>
              <Form.Control
                type="text"
                placeholder="Search..."
                name="query"
                onChange={(e) => handleInputChange(e, handleChange)}
                onFocus={() => setShowResults(true)}
                value={values.query}
                autoFocus={onSearchClose ? true : false}
              />
              {onSearchClose && (
                <Button
                  variant="light"
                  className="text-dark input-close-btn border-0 position-absolute top-50 translate-middle-y"
                  type="button"
                  size="sm"
                  onClick={onSearchClose}
                  style={{ right: '2.5rem' }}
                >
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
                  </svg>
                </Button>
              )}
              <Button
                variant="outline-light"
                className="text-dark input-search-btn border-0 position-absolute top-50 translate-middle-y"
                type="submit"
                size="sm"
                disabled={isSubmitting}
                style={{ right: onSearchClose ? '0.25rem' : '0.25rem' }}
              >
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                </svg>
              </Button>

              {showResults && resultItems.length > 0 && (
                <div
                  className="suggestions-panel position-absolute bg-white border border-top-0 shadow-sm"
                  style={{
                    zIndex: 1000,
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginTop: "5px",
                    borderRadius: "8px",
                  }}
                >
                  {resultItems.map((result, index) => (
                    <div
                      key={index}
                      className="suggestion-item px-3 py-2 cursor-pointer hover-bg-light"
                      style={{ cursor: "pointer" }}
                    >
                      <ResultCard 
                        item={result} 
                        followingStatus={followingStatus}
                        setFollowingStatus={setFollowingStatus}
                        user={user}
                      />
                    </div>
                  ))}

                  <div className="text-center py-2">
                    <Link to={`/search-users?query=${values.query}`} className="text-decoration-none">
                      View All
                    </Link>
                  </div>
                </div>
              )}
            </Form.Group>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const ResultCard = ({ item, followingStatus = {}, setFollowingStatus, user }) => {
  const navigate = useNavigate();
  const [localConnStatus, setLocalConnStatus] = useState(item.connection_status);
  const [pendingConnId, setPendingConnId] = useState(item.pending_connection_id ?? null);

  useEffect(() => {
    setLocalConnStatus(item.connection_status);
    setPendingConnId(item.pending_connection_id ?? null);
  }, [item.id, item.connection_status, item.pending_connection_id]);

  let username = item.username;
  let profileType = "";
  let name = "";

  if (item.role === "candidate"){
    profileType = "Candidate";
    name = (item?.first_name+" "+item?.last_name).trim();
    if (name) 
      username = name;
  }
  else{
    profileType = "Employer/Company";
    name = item.company_name;
    if (name)
      username = `${item?.company_name?.trim()?.charAt(0)} ${item?.company_name?.charAt(1)}`;
    else
      name = (item?.first_name+" "+item?.last_name).trim();
  }

  const handleConnection = (event, userId) => {
    event.preventDefault();
    event.stopPropagation();
    AddConnection({ to_user: userId })
      .then((resp) => {
        toast.show({
          type: "success",
          title: "Connection request sent",
        });
        setLocalConnStatus("request_sent");
        setPendingConnId(resp?.data?.id ?? null);
      })
      .catch((error) => {
        toast.show({
          type: "danger",
          title: "Unable to send connection request",
          description: ErrorHandling(error),
        });
      });
  };

  const handleWithdrawRequest = async (event, userId) => {
    event.preventDefault();
    event.stopPropagation();
    if (!pendingConnId) {
      toast.show({
        type: "warning",
        title: "Cannot withdraw",
        description: "Try again from the full search page or profile.",
      });
      return;
    }
    try {
      await DeleteConnection(pendingConnId);
      setLocalConnStatus(null);
      setPendingConnId(null);
      toast.show({ type: "success", title: "Request withdrawn" });
    } catch (error) {
      toast.show({
        type: "danger",
        title: "Unable to withdraw request",
        description: ErrorHandling(error),
      });
    }
  };

  const handleFollow = async (event, userId) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await FollowUser(userId);
      setFollowingStatus(prev => ({ ...prev, [userId]: true }));
      toast.show({
        type: "success",
        title: "Successfully followed",
      });
    } catch (error) {
      toast.show({
        type: "danger",
        title: "Unable to follow",
        description: ErrorHandling(error),
      });
    }
  };

  const handleUnfollow = async (event, userId) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await UnfollowUser(userId);
      setFollowingStatus(prev => ({ ...prev, [userId]: false }));
      toast.show({
        type: "success",
        title: "Successfully unfollowed",
      });
    } catch (error) {
      toast.show({
        type: "danger",
        title: "Unable to unfollow",
        description: ErrorHandling(error),
      });
    }
  };

  // Determine what action buttons to show based on user types
  const getActionButtons = () => {
    const currentUserIsEmployer = user?.employer_profile !== null;
    const currentUserIsCandidate = user?.user_profile !== null;
    const targetIsEmployer = item.role === "employer" || item.employer_profile !== null;
    const targetIsCandidate = item.role === "candidate" || item.user_profile !== null;
    const isFollowing = followingStatus[item.id] || false;
    // const isConnected = item.is_connected;

    // Cannot interact with yourself
    if (item.id === user?.id) {
      return null;
    }

    // Employer to candidate: No connection or follow
    if (currentUserIsEmployer && targetIsCandidate) {
      return null;
    }

    // Employers to employer: Only follow
    if (currentUserIsEmployer && targetIsEmployer) {
      return (
        <button
          type="button"
          className={`btn btn-sm ${isFollowing ? "btn-outline-secondary" : "btn-outline-primary"}`}
          onClick={(event) => isFollowing ? handleUnfollow(event, item.id) : handleFollow(event, item.id)}
        >
          {isFollowing ? "Unfollow" : "Follow"}
        </button>
      );
    }

    // Candidate to employer: Only follow
    if (currentUserIsCandidate && targetIsEmployer) {
      return (
        <button
          type="button"
          className={`btn btn-sm ${isFollowing ? "btn-outline-secondary" : "btn-outline-primary"}`}
          onClick={(event) => isFollowing ? handleUnfollow(event, item.id) : handleFollow(event, item.id)}
        >
          {isFollowing ? "Unfollow" : "Follow"}
        </button>
      );
    }

    // Candidate to candidate: Connection request
    if (currentUserIsCandidate && targetIsCandidate) {
      const connectionStatus = localConnStatus;

      if (connectionStatus === "connected") {
        return <span className="text-muted small">Connected</span>;
      }

      if (connectionStatus === "request_sent") {
        return (
          <div className="d-flex flex-column align-items-end gap-1">
            <span className="text-muted small">Request sent</span>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm py-0 px-2"
              onClick={(event) => handleWithdrawRequest(event, item.id)}
              disabled={!pendingConnId}
            >
              Withdraw
            </button>
          </div>
        );
      }

      return (
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={(event) => handleConnection(event, item.id)}
        >
          Connect
        </button>
      );
    }

    return null;
  };

  return (
    <div className="d-flex align-items-center justify-content-between py-1 px-1 border-bottom search-result-item">
      <div 
        className="d-flex align-items-center"
        onClick={() => navigate(`/profile/${item.role}/${item.id}`)}
        style={{ cursor: 'pointer', flex: 1 }}
      >
        <div className="me-3">
          <Avatar 
            src={item?.get_avatar?.public_url || null} 
            username={username} 
            size="lg" 
            center={true} 
          />
        </div>

        <div>
          <p className="mb-0 fw-medium text-dark" style={{ fontSize: "14px" }}>
            {name || item.username}
          </p>
          <p className="mb-0 text-muted small">{profileType}</p>
        </div>
      </div>

      <div style={{ marginLeft: "5px" }} onClick={(e) => e.stopPropagation()}>
        {getActionButtons()}
      </div>
    </div>
  );
};

export default Header;
